import { useState, useEffect, useCallback, useRef } from 'react';

export function useSearch(filters = {}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const abortControllerRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://artfolio-api.spb-f86.workers.dev';

  const performSearch = useCallback(async (searchQuery, searchCursor, isNewSearch) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      let url = `${apiUrl}?search=${encodeURIComponent(searchQuery)}`;
      if (searchCursor) {
        url += `&cursor=${encodeURIComponent(searchCursor)}`;
      }

      // Add filters to the URL
      if (filters.artistFilter) {
        url += `&artistFilter=${encodeURIComponent(filters.artistFilter)}`;
      }
      if (filters.movementFilter) {
        url += `&movementFilter=${encodeURIComponent(filters.movementFilter)}`;
      }
      if (filters.mediumFilter) {
        url += `&mediumFilter=${encodeURIComponent(filters.mediumFilter)}`;
      }

      const response = await fetch(url, {
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();

      if (isNewSearch) {
        setResults(data.results || []);
      } else {
        setResults(prev => [...prev, ...(data.results || [])]);
      }

      setCursor(data.nextCursor || null);
      setHasMore(data.hasMore || false);
    } catch (err) {
      if (err.name === 'AbortError') {
        // Ignore aborted requests
        return;
      }
      setError(err.message);
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, filters]);

  // Debounced search when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setCursor(null);
      setHasMore(false);
      setError(null);
      return;
    }

    // Debounce: wait 300ms before searching
    const timeoutId = setTimeout(() => {
      performSearch(query, null, true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, performSearch, filters]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore && cursor && query.trim()) {
      performSearch(query, cursor, false);
    }
  }, [isLoading, hasMore, cursor, query, performSearch]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setCursor(null);
    setHasMore(false);
    setError(null);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    hasMore,
    error,
    loadMore,
    clearSearch
  };
}
